import { create } from 'zustand';
import { QueryClient } from '@tanstack/react-query';
import { createClient, endpoints, ApiError, type Me, type Session } from '@immoaktas/api-client';
import i18n from 'i18next';
import { ENV } from './env';
import { firebaseSignOut } from './firebase';

/** Session state: access token only in memory, refresh token as httpOnly cookie on api.<domain> (B9). */
type AuthState = {
  token: string | null;
  me: Me | null;
  accountId: string | null;
  ready: boolean;
  set: (p: Partial<AuthState>) => void;
};
export const useAuth = create<AuthState>((set) => ({ token: null, me: null, accountId: localStorageGet('ia:accountId'), ready: false, set: (p) => set(p) }));

function localStorageGet(k: string) {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}

const client = createClient({
  baseUrl: ENV.apiUrl,
  withCredentials: true,
  getLanguage: () => i18n.language,
  getAccessToken: () => useAuth.getState().token,
  getAccountId: () => useAuth.getState().accountId,
  refresh: async () => {
    try {
      const s = await endpoints(client).auth.refresh();
      useAuth.getState().set({ token: s.accessToken });
      return s.accessToken;
    } catch {
      return null;
    }
  },
  onSessionEnd: () => void endSession(),
});
export const api = endpoints(client);
export const raw = client;

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (n, e) => !(e instanceof ApiError && [400, 401, 402, 403, 404, 409, 423].includes(e.status)) && n < 2,
      refetchOnWindowFocus: true,
    },
  },
});

export async function startSession(s: Session) {
  useAuth.getState().set({ token: s.accessToken });
  await loadMe();
}

export async function loadMe() {
  const me = await api.me.get();
  const st = useAuth.getState();
  let accountId = st.accountId;
  if (!accountId || !me.memberships.some((m) => m.accountId === accountId)) accountId = me.memberships[0]?.accountId ?? null;
  setAccount(accountId);
  st.set({ me, ready: true });
  return me;
}

export function setAccount(id: string | null) {
  const prev = useAuth.getState().accountId;
  try {
    if (id) localStorage.setItem('ia:accountId', id);
    else localStorage.removeItem('ia:accountId');
  } catch {
    /* storage blocked: keep in memory */
  }
  useAuth.getState().set({ accountId: id });
  // Only a real switch drops the data of the previous account. Reset (not remove) so mounted views
  // (sidebar counts, current page) refetch for the new account instead of waiting forever.
  if (prev !== id) void queryClient.resetQueries({ predicate: (q) => q.queryKey[0] !== 'me' });
}

/** Startup: try the refresh cookie; no cookie = logged out (never an error screen). Runs once (StrictMode-safe). */
let booting: Promise<void> | null = null;
export function bootstrap() {
  booting ??= (async () => {
    try {
      const s = await api.auth.refresh();
      useAuth.getState().set({ token: s.accessToken });
      await loadMe();
    } catch {
      useAuth.getState().set({ ready: true });
    }
  })();
  return booting;
}

export async function endSession() {
  try {
    await api.auth.logout();
  } catch {
    /* already gone */
  }
  await firebaseSignOut();
  queryClient.clear();
  useAuth.getState().set({ token: null, me: null, ready: true });
}

export const role = () => {
  const s = useAuth.getState();
  return s.me?.memberships.find((m) => m.accountId === s.accountId)?.role ?? null;
};
